import { Input, message, UploadFile, Upload } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { InboxOutlined } from "@ant-design/icons";
import { QuoteCreationStepStateType } from "@/common/types/quote";
import { isString } from "lodash";
import { usePermissions } from "@/common/contexts/Permissions.context";
import SearchCustomerByName from "@/components/Customer/SearchCustomerByName/SearchCustomerByName";
import Typography from "@/components/Typography/Typography";

const stateKeys = ["firstStepData", "secondStepData"] as const;
type StateKeys = (typeof stateKeys)[number];

interface QuoteModalSecondStepProps {
  globalState: QuoteCreationStepStateType;
  validationErr: Record<string, string>;
  updateFunc: (parentName: StateKeys, fieldName: string, value: any) => void;
  removeErrors: (fieldName: string) => void;
}
export default function QuoteModalSecondStep({
  globalState,
  updateFunc,
  validationErr,
  removeErrors,
}: QuoteModalSecondStepProps) {
  const { company } = usePermissions();
  const handleBeforeUpload = (selectedFile: File) => {
    removeErrors("file");
    const maxSizeInBytes = 5 * 1024 * 1024;
    const allowedTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel.sheet.macroEnabled.12",
      "application/vnd.ms-excel.template.macroEnabled.12",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.template",
    ];
    if (!allowedTypes.includes(selectedFile.type)) {
      message.error("Only tabular formats are allowed");
      updateFunc("secondStepData", "file", null);
      return Upload.LIST_IGNORE;
    }
    if (selectedFile.size > maxSizeInBytes) {
      message.error("File size exceeds 5MB limit");
      updateFunc("secondStepData", "file", null);
      validationErr.file;
      return Upload.LIST_IGNORE;
    }
    updateFunc("secondStepData", "file", selectedFile);
    removeErrors("file");
    message.success("File ready to be uploaded");
    return false;
  };
  return (
    <div>
      {company.companyType === "airx" && (
        <SearchCustomerByName
          className="bg-gray-border "
          labelClassName="text-black font-light"
          onChange={(value) => {
            updateFunc("secondStepData", "customer", value);
          }}
          value={
            globalState.secondStepData.customer?.label as unknown as number
          }
        />
      )}
      <div className="grid grid-cols-2 gap-4 my-4">
        <div>
          <label>
            <Typography
              type="span"
              fontFamily="font-kanit"
              textColor="black"
              fontWeight="font-light"
            >
              End customer Name
              <Typography type="span" textColor="red-main">
                *
              </Typography>
            </Typography>
            <Input
              onChange={(event) => {
                updateFunc(
                  "secondStepData",
                  "endCustomerName",
                  event.target.value
                );
                removeErrors("endCustomerName");
              }}
              placeholder="End Customer Name"
              name="endCustomerName"
              className="bg-gray-border !border-none px-4 py-3 !disabled:text-black text-black"
            />
            {isString(validationErr.endCustomerName) && (
              <Typography
                type="span"
                fontFamily="font-kanit"
                textColor="red-main"
              >
                {validationErr.endCustomerName}
              </Typography>
            )}
          </label>
        </div>
        <div>
          <label>
            <Typography
              type="span"
              fontFamily="font-kanit"
              textColor="black"
              fontWeight="font-light"
            >
              Customer's Project Num./Ref.{" "}
              <Typography type="span" textColor="red-main">
                *
              </Typography>
            </Typography>
            <Input
              placeholder="Customer project number"
              onChange={(event) => {
                updateFunc(
                  "secondStepData",
                  "customerProjectNumber",
                  event.target.value
                );
                removeErrors("customerProjectNumber");
              }}
              name="customerProjectNumber"
              className="bg-gray-border !border-none px-4 py-3 !disabled:text-black text-black"
            />
            {isString(validationErr.customerProjectNumber) && (
              <Typography
                type="span"
                fontFamily="font-kanit"
                textColor="red-main"
              >
                {validationErr.customerProjectNumber}
              </Typography>
            )}
          </label>
        </div>
      </div>
      <div>
        <Dragger
          fileList={
            globalState.secondStepData.file
              ? ([globalState.secondStepData.file] as unknown as UploadFile[])
              : []
          }
          beforeUpload={handleBeforeUpload}
          multiple={false}
          onRemove={() => updateFunc("secondStepData", "file", null)}
        >
          <Typography type="p" customStyles="ant-upload-drag-icon">
            <InboxOutlined />
          </Typography>
          <Typography type="p" customStyles="ant-upload-text">
            Click or drag file to this area to upload
          </Typography>
          <Typography type="p" customStyles="ant-upload-hint">
            Only CVS/Exel files are allowed. Max size before upload: 5MB.
          </Typography>
        </Dragger>
        {isString(validationErr.file) && (
          <Typography type="span" fontFamily="font-kanit" textColor="red-main">
            {validationErr.file}
          </Typography>
        )}
      </div>
    </div>
  );
}
